import { ApolloClient, useReactiveVar } from '@apollo/client';
import {
  allowedEventsStore,
  documentVisibilityStore,
  enableMultiGameStore,
  hpContents,
  multiGameActiveStore,
} from 'apolloGraphql/mml-apollo-cache';
import { AppContext } from 'context/app-context';
import { useContext, useEffect } from 'react';
import { getMaxStreams } from 'state-machines/just-multi-game/utils';
import {
  AppFeaturedDocument,
  AppFeaturedQuery,
  BracketDataDocument,
  BracketDataQuery,
  CurrentScoresDocument,
  CurrentScoresQuery,
  FeaturedSpotlight,
  FeaturedTrending,
  SpotlightContent,
  StaticTestEnv,
  useAppFeaturedQuery,
  VodList,
} from 'types/generated-types';
import { logger } from '../logger';
import usePerformanceBucket from './usePerformanceBucket';

const LOG = logger.getLogger('useAppFeaturedData');

interface Params {
  pollInterval: number;
  queryParams: { seasonYear: number; staticTestEnv?: StaticTestEnv };
  allowed_events: number[];
}

export function useAppFeaturedData() {
  const { appConfig: config } = useContext(AppContext);
  const queryParams = {
    seasonYear: +(config?.gql.season_year ?? 0),
    staticTestEnv: config?.gql.static_test_env,
  };
  const allowedEvents = useReactiveVar(allowedEventsStore);
  useAppFeaturedDataInner({
    pollInterval: config?.gql.links.home_featured.poll_interval ?? 60,
    queryParams: queryParams,
    allowed_events: allowedEvents,
  });
}

function useAppFeaturedDataInner({ pollInterval, queryParams }: Params) {
  const isDocumentVisible = useReactiveVar(documentVisibilityStore);
  const allowedEvents = useReactiveVar(allowedEventsStore);
  const performanceBucket = usePerformanceBucket();
  const { data: hpData } = useAppFeaturedQuery({
    variables: { ...queryParams, view: null },
    pollInterval: pollInterval * 1000,
    fetchPolicy: 'cache-and-network',
    skip: !isDocumentVisible,
  });

  useEffect(() => {
    if (!hpData?.appFeatured) return;

    const hpArray = normalizeHpContents(
      hpData,
      allowedEvents,
      !!enableMultiGameStore(),
      !!multiGameActiveStore(),
      getMaxStreams(performanceBucket) > 1,
    );

    hpContents(hpArray);
    return () => {
      LOG?.info('unsubscribing');
    };
  }, [allowedEvents, hpData, performanceBucket]);
}

function normalizeHpContents(
  hpData: AppFeaturedQuery,
  allowed_events: number[],
  enableMultiGame: boolean,
  multiGameActive: boolean,
  allowMultipleStreams: boolean,
) {
  const hpArray: (FeaturedSpotlight | FeaturedTrending | VodList)[] = [];
  const features = hpData?.appFeatured?.spotlight as FeaturedSpotlight;
  const spotlightContent = features
    ? (features.content as SpotlightContent[])
        .filter(
          (item) =>
            item &&
            ((item.contentType === 'game' && filterEvents(item as SpotlightContent, allowed_events)) ||
              (item.contentType === 'event' && filterEvents(item as SpotlightContent, allowed_events)) ||
              (item.contentType === 'montage' && filterEvents(item as SpotlightContent, allowed_events)) ||
              (item.contentType === 'nomultigamePromo' && enableMultiGame && multiGameActive && allowMultipleStreams) || // intentionally wrong content type to omit but still keep params
              item.contentType === 'vod' ||
              item.contentType === 'bcgPromo' ||
              item.contentType === 'shopPromo'),
        )
        .sort((a, b): number => {
          if (a && b) {
            return (a.sort as number) - (b.sort as number);
          }
          return -1;
        })
    : [];

  const trending = hpData?.appFeatured?.trending as FeaturedTrending;
  const vodPlaylist = hpData?.appFeatured?.vodPlaylist as VodList[];

  hpArray[features?.sortObject ?? 0] = { ...features, content: spotlightContent };
  hpArray[trending?.sortObject ?? 0] = { ...trending };

  vodPlaylist?.forEach((playlist) => {
    hpArray[playlist?.sortObject ?? 0] = { ...playlist };
  });

  return hpArray;
}

const filterEvents = (item: SpotlightContent, allowed_events: number[]): boolean => {
  const bracketId = item.bracketId;

  if (bracketId) {
    if (bracketId > 703 && bracketId < 902) {
      if (allowed_events.includes(bracketId)) return true;
      return false;
    }
    return true;
  }
  return false;
};

export async function preloadAppFeaturedData(
  client: ApolloClient<object>,
  seasonYear: number,
  staticTestEnv?: StaticTestEnv,
) {
  const { data } = await client.query<AppFeaturedQuery>({
    query: AppFeaturedDocument,
    variables: { seasonYear, staticTestEnv, view: null },
  });

  hpContents(normalizeHpContents(data, allowedEventsStore(), !!enableMultiGameStore(), !!multiGameActiveStore(), true));
}

export async function preloadCurrentData(
  client: ApolloClient<object>,
  seasonYear: number,
  staticTestEnv?: StaticTestEnv,
) {
  await client.query<CurrentScoresQuery>({
    query: CurrentScoresDocument,
    variables: { seasonYear, staticTestEnv, current: true },
  });
}

export async function preloadBracketData(
  client: ApolloClient<object>,
  seasonYear: number,
  staticTestEnv?: StaticTestEnv,
) {
  await client.query<BracketDataQuery>({
    query: BracketDataDocument,
    variables: { seasonYear, staticTestEnv },
  });
}
